import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'hot' | 'compact';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  const formatViews = (views: number): string => {
    if (views >= 10000) {
      return `${(views / 10000).toFixed(1)}万`;
    }
    return views.toString();
  };

  if (variant === 'hot') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group flex gap-4 p-4 bg-background-50 rounded-lg hover:bg-primary-50/50 transition-colors cursor-pointer"
      >
        <div className="w-[100px] h-[140px] flex-shrink-0 overflow-hidden rounded-md">
          <img
            src={comic.cover}
            alt={`${comic.title} - 韩国漫画`}
            title={`${comic.title} - 免费在线阅读`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex flex-col justify-between py-1 flex-1 min-w-0">
          <div>
            <h3 className="font-heading text-base text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
              {comic.title}
            </h3>
            <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
            <p className="text-xs text-foreground-400 mt-2 line-clamp-2 leading-relaxed">{comic.description}</p>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <span className="inline-flex items-center gap-1 text-xs text-accent-600">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-star-fill" style={{ fontSize: '10px' }}></i>
              </div>
              {comic.rating}
            </span>
            <span className="text-xs text-foreground-400">{formatViews(comic.views)} 阅读</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
            }`}>
              {comic.status}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group flex items-center gap-3 py-2.5 border-b border-secondary-100 last:border-b-0 cursor-pointer"
      >
        <span className="font-heading text-sm text-foreground-400 w-5 flex-shrink-0 text-center whitespace-nowrap">
          {comic.id.split('-').pop()}
        </span>
        <div className="w-10 h-14 flex-shrink-0 overflow-hidden rounded">
          <img
            src={comic.cover}
            alt={`${comic.title} - 韩国漫画`}
            title={`${comic.title} - 免费在线阅读`}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
            {comic.title}
          </h4>
          <p className="text-xs text-foreground-400 mt-0.5">{comic.author}</p>
        </div>
        <span className="text-xs text-accent-600 font-medium whitespace-nowrap">{comic.rating}</span>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block cursor-pointer"
    >
      <div className="relative aspect-[3/4] overflow-hidden rounded-lg bg-secondary-100">
        <img
          src={comic.cover}
          alt={`${comic.title} - ${comic.category}韩国漫画`}
          title={`${comic.title} - 免费在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2">
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            comic.status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-secondary-800 text-secondary-200'
          }`}>
            {comic.status}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-star-fill text-accent-400" style={{ fontSize: '10px' }}></i>
            </div>
            <span className="text-xs text-background-50 font-medium">{comic.rating}</span>
            <span className="text-xs text-background-50/60 ml-auto">{formatViews(comic.views)}</span>
          </div>
        </div>
      </div>
      <div className="mt-3">
        <h3 className="font-heading text-sm text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-400 mt-1">{comic.author}</p>
        <div className="flex items-center flex-wrap gap-1.5 mt-2">
          {comic.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="text-xs px-1.5 py-0.5 bg-secondary-100 text-secondary-700 rounded">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}